import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { catchRenderException, HpmsCustomDialogAPI, HPMSOnClickLink, HpmsReportListAPI, HPMSReportOnClick, HpmsTermCheckboxAPI, HpmsTextHtmlAPI } from '@philips-emr/hpms-health-insurance-components';
import { ActionAPI, ButtonAPI, CheckboxAPI, DynamicFieldsAPI, FetchAPI, FieldAPI, FormAPI, Metadata, ModalAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnClick, OnClose, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { AuthenticationService, HttpService, SessionStorageService } from '@philips-emr/tws-core/lib/services';

import { HealthDeclarationDynamicFormAliases as Aliases, FragmentNames } from '../../codegen';
import { BaseRoute } from '../../route';
import { ToastService } from '../../services';
import { report } from '../../services/report';
import NoLoginBaseController from '../no-login-base-controller';
import './health-declaration.scss';

interface DynamicFieldsData {
  id: string | number;
  title: string;
}

@Controller({ fragment: FragmentNames.HEALTH_DECLARATION_DYNAMIC_FORM })
export default class HealthDeclarationDynamicFormController extends NoLoginBaseController {

  private preventLeaving: boolean = true;
  private showPrint: boolean = true;
  private showPrintDialog: boolean = false;
  private printList: any[];

  @Inject(Aliases.FORM_QUESTIONNAIRE)
  private formQuestionnaire: FormAPI;

  @Inject(Aliases.DYNAMIC_FIELDS_QUESTIONNAIRE)
  private dynamicFields: DynamicFieldsAPI;

  @Inject(Aliases.TOAST_NOTIFICATION_PRIVACY_WARNING)
  private toastNotificationPrivacyWarning: ToastNotificationAPI;

  @Inject(Aliases.TOAST_NOTIFICATION_FORM_FEEDBACK)
  private toastNotificationFormFeedback: ToastNotificationAPI;

  @Inject(Aliases.FORM_PRIVACY_WARNING)
  private formPrivacyWarning: FormAPI;

  @Inject(Aliases.FIELD_CHECKBOX)
  private fieldCheckBox: FieldAPI;

  @Inject(Aliases.CHECKBOX)
  private checkBoxPrivacyWarning: CheckboxAPI;

  @Inject(Aliases.HPMS_TEXT_HTML)
  private textHtml: HpmsTextHtmlAPI;

  @Inject(Aliases.PANEL_MODAL_TERM)
  private panelModal: PanelAPI;

  @Inject(Aliases.MODAL_TERM)
  private modalTerm: ModalAPI;

  @Inject(Aliases.BUTTON_PRINT)
  private buttonPrint: ActionAPI;

  @Inject(Aliases.FORM_USER)
  private formUser: FormAPI;

  @Inject(Aliases.HPMS_CHECKBOX)
  private checkBox: HpmsTermCheckboxAPI;

  @Inject(Aliases.FIELD_PRIVACY_WARNING)
  private fieldPrivacyWarning: FieldAPI;

  @Inject(Aliases.PANEL_FINISH)
  private panelFinish: PanelAPI;

  @Inject(Aliases.HPMS_CUSTOM_DIALOG_SEND)
  private customDialogSend: HpmsCustomDialogAPI;

  @Inject(Aliases.HPMS_CUSTOM_DIALOG_CANCEL)
  private customDialogCancel: HpmsCustomDialogAPI;

  @Inject(Aliases.HPMS_CUSTOM_DIALOG_PRINT)
  private customDialogPrint: HpmsCustomDialogAPI;

  @Inject(Aliases.FETCH_QUESTIONNAIRE)
  private fetchQuestionnaire: FetchAPI;

  @Inject(Aliases.HPMS_REPORT_LIST)
  private reportList: HpmsReportListAPI;

  @OnMount(Aliases.ROOT_COMPONENT)
  onRootMount(): void {
    this.noLoginThemeSetUp();
  }

  @OnClick(Aliases.BUTTON_FINISH)
  onFinishButtonClick(): void {
    this.preventLeaving = false;
    this.onLeaving();
    this.historyService.push(BaseRoute.HEALTH_DECLARATION_FINALIZATION);
  }

  @OnCancel(Aliases.FORM_QUESTIONNAIRE)
  onCancelFormQuestionnaire(): void {
    this.customDialogCancel.setVisible(true);
  }

  @OnClick(Aliases.BUTTON_NO_CANCEL)
  onNoCancelClick(): void {
    this.customDialogCancel.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_YES_CANCEL)
  onYesCancelClick(): void {
    this.preventLeaving = false;
    this.onLeaving();
    this.historyService.push(BaseRoute.HEALTH_DECLARATION_CANCELATION);
  }

  @OnValidate(Aliases.FORM_USER)
  onFormValidate(): void {
    this.calculateIMC();
  }

  calculateIMC(): void {
    let { qtWeight } = this.formUser.getRecord();
    const { qtHeightCm } = this.formUser.getRecord();
    const record = this.formUser.getRecord();

    try {
      if (qtWeight !== undefined && qtHeightCm !== undefined) {
        qtWeight = Number(qtWeight);

        this.formUser.setRecord({ ...record, qtImc: (qtWeight / ((qtHeightCm as number / 100) * (qtHeightCm as number / 100))).toPrecision(3) });
      } else {
        this.formUser.setRecord({ ...record, qtImc: 0 });
      }
    } catch {
      this.formUser.setRecord({ ...record, qtImc: 0 });
    }
  }

  @OnSubmit(Aliases.FORM_QUESTIONNAIRE)
  onSendClick(): void {
    if (this.fieldPrivacyWarning.isVisible() && !this.checkBox.isChecked()) {
      return;
    }
    if ((!this.formUser.isValid() || !this.formQuestionnaire.isValid())) {
      return;
    }
    this.customDialogSend.setVisible(true);
  }

  @OnClick(Aliases.BUTTON_YES_SEND)
  onYesSendClick(): void {
    this.submitForm();
  }

  @OnClick(Aliases.BUTTON_NO_SEND)
  onNoSendClick(): void {
    this.customDialogSend.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_PRINT)
  onPrintButtonClick(event?: ClickEvent): void {
    if (!this.showPrintDialog) {
      const seqInsuredDeclaration = SessionStorageService.getItem('seqInsuredDeclaration') as number;
      const cdPhysicalPerson = SessionStorageService.getItem('cdPhysicalPerson') as number;
      const namePhysicalPerson = this.context.get('namePhysicalPerson') as string;

      let { hash } = this.historyService.getCurrentRouteParams();
      hash = hash as string;

      if (event) {
        const api = event.target as ButtonAPI;
        api.setEnabled(false);
        report(namePhysicalPerson, this.printList[0].cdRelatorio, cdPhysicalPerson, seqInsuredDeclaration, { hash, seqInsuredDeclaration }).finally(() => api.setEnabled(true));
      } else {
        report(namePhysicalPerson, this.printList[0].cdRelatorio, cdPhysicalPerson, seqInsuredDeclaration, { hash, seqInsuredDeclaration }).finally(() => this.buttonPrint.setEnabled(true));
      }
    } else {
      this.customDialogPrint.setVisible(true);
    }
  }

  @OnMount(Aliases.BUTTON_NO_PRINT)
  onNoPrintButtonMount(): void {
    this.reportList.setData(this.printList);
    this.reportList.setLoading(false);
  }

  @HPMSReportOnClick(Aliases.HPMS_REPORT_LIST)
  onReportListClick(event: ClickEvent): void {
    const { cdRelatorio } = event.value;
    const seqInsuredDeclaration = SessionStorageService.getItem('seqInsuredDeclaration') as number;
    const cdPhysicalPerson = SessionStorageService.getItem('cdPhysicalPerson') as number;
    const namePhysicalPerson = this.context.get('namePhysicalPerson') as string;

    let { hash } = this.historyService.getCurrentRouteParams();
    hash = hash as string;

    report(namePhysicalPerson, cdRelatorio, cdPhysicalPerson, seqInsuredDeclaration, { hash, seqInsuredDeclaration }).finally(() => this.buttonPrint.setEnabled(true));
    this.customDialogPrint.setVisible(false);
  }

  @HPMSOnClickLink(Aliases.HPMS_CHECKBOX)
  onCheckBoxLinkClick(): void {
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.TOAST_NOTIFICATION_PRIVACY_WARNING)
  onCloseToastNotificationPrivacyWarning(): void {
    this.toastNotificationPrivacyWarning.setVisible(false);
  }

  scrollDownPanelTerm(): void {
    const termPanel = document.querySelector<HTMLDivElement>('.c-modal__content--with-stack-manager');
    if (termPanel) {
      const { scrollHeight } = termPanel;
      termPanel.scrollTo({ top: scrollHeight, behavior: 'smooth' });
    }
  }

  @OnMount(Aliases.FETCH_PRIVACY_WARNING)
  onFetchPrivacyWarningMount(event: MountEvent): void {
    const fetch = event.target as FetchAPI;
    fetch.setParameters({ nrSeqTerm: this.context.get('nrTerm') });
    fetch.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_PRIVACY_WARNING)
  onAfterFetchPrivacyWarning(event: FetchEvent): void {
    const { title, content } = event.data as any;

    this.modalTerm.setTitle(title);
    this.textHtml.setData(content);

    this.checkBoxPrivacyWarning.setValue(this.checkBox.isChecked());
    this.formPrivacyWarning.setRecord({ ...this.formPrivacyWarning.getRecord(), checked: this.checkBox.isChecked() });
  }

  @OnSubmit(Aliases.FORM_PRIVACY_WARNING)
  onFormPrivacyWarningSubmit(): void {
    const { checked } = this.formPrivacyWarning.getRecord();
    if (!(checked as boolean)) {
      this.formPrivacyWarning.setState({ isValid: false });
      this.fieldCheckBox.setState({ showValidations: true });
      this.toastNotificationPrivacyWarning.setVisible(true);
      this.scrollDownPanelTerm();
      return;
    }

    this.checkBox.setChecked(checked as boolean);
    this.checkBox.setEnabled(false);
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_PRIVACY_WARNING)
  onCancelFormPrivacyWarning(): void {
    this.onModalTermoClose();
  }

  @OnClose(Aliases.MODAL_TERM)
  onModalTermoClose(): void {
    this.panelModal.setVisible(false);
  }

  beforeLeaving(e: BeforeUnloadEvent): any {
    if (!this.preventLeaving) {
      e.preventDefault();
      return null;
    }
    const textThatIsNeededButNotShown = '';

    e.returnValue = textThatIsNeededButNotShown;
    return textThatIsNeededButNotShown;
  }

  onLeaving(): void {
    this.preventLeaving = false;
    window.onbeforeunload = null;
    void AuthenticationService.logout();
    SessionStorageService.clear();
  }

  @OnMount(Aliases.PANEL_0)
  onMountAccessibility(): void {
    window.onunload = this.onLeaving;
    window.onbeforeunload = e => this.beforeLeaving(e);

    let { hash } = this.historyService.getCurrentRouteParams();
    hash = hash as string;
    const seqInsuredDeclaration = SessionStorageService.getItem('seqInsuredDeclaration') as number;

    HttpService.post(ENDPOINTS.GET.HEALTH_DECLARATION_USER_INFO, {},
      { body: { hash, seqInsuredDeclaration } })
      .then(response => response.body as any)
      .then(body => {
        this.formUser.setData({ ...body });
        const { nrSeqModel, namePhysicalPerson } = body;
        this.context.set({ nrSeqModel });
        this.context.set({ namePhysicalPerson });
        this.loadDynamicForm();
        HttpService.get(ENDPOINTS.GET.REPORTS)
          .then(response => {
            if (response.body) {
              const innerBody = response.body as any[];

              if (innerBody.length == 0) {
                this.showPrint = false;
              }
              this.printList = innerBody;
              if (innerBody.length > 1) {
                this.printList = body;
                this.showPrintDialog = true;
              }
            }
          })
          .catch(() => {
            this.showPrint = false;
          });
      }).catch(() => {
        this.onLeaving();
        this.historyService.push(BaseRoute.HEALTH_DECLARATION_VALID, { hash });
      });
  }

  @OnClose(Aliases.TOAST_NOTIFICATION_FORM_FEEDBACK)
  onCloseToastNotificationFormFeedback(): void {
    this.toastNotificationFormFeedback.setVisible(false);
  }

  @OnBeforeSubmit(Aliases.FORM_QUESTIONNAIRE)
  onBeforeSubmit(): void {
    this.toastNotificationFormFeedback.setVisible(!this.checkBox.isChecked());
  }

  submitForm(): void {
    const { record } = this.formQuestionnaire.state;
    const { qtHeightCm, qtWeight, qtImc } = this.formUser.getRecord();
    const seqInsuredDeclaration = SessionStorageService.getItem('seqInsuredDeclaration') as number;
    const dynamicFieldsData = this.dynamicFields.getMetadata() as DynamicFieldsData;
    const { nrTerm } = this.context.getBulkData();
    let { hash } = this.historyService.getCurrentRouteParams();
    hash = hash as string;

    this.formQuestionnaire.setLocked(false);
    this.formQuestionnaire.setEditing(false);
    this.formQuestionnaire.setLocked(true);
    this.formUser.setEditing(false);
    this.formQuestionnaire.setLoading(true);
    const bodyQuestionnaire = {
      nrSeq: seqInsuredDeclaration,
      wModeloSubmitDTO: {
        formSubmit: record || {},
        dynamicFieldId: dynamicFieldsData.id,
      },
    };
    const bodyHealthDeclaration = {
      hash,
      seqInsuredDeclaration,
      userName: this.context.get('namePhysicalPerson'),
      qtWeight,
      qtHeight: qtHeightCm,
      qtImc,
      nrSeqTerm :nrTerm,
    };

    HttpService.post(ENDPOINTS.POST.HEALTH_DECLARATION_QUESTIONNAIRE_DATA, { seqInsuredDeclaration }, { body: bodyQuestionnaire })
      .then(() => {
        HttpService.post(ENDPOINTS.POST.HEALTH_DECLARATION_SUBMIT, {}, { body: bodyHealthDeclaration })
          .then(() => {
            this.customDialogSend.setVisible(false);

            if (this.showPrint) {
              this.buttonPrint.setEnabled(true);
            }

            this.panelFinish.setVisible(true);
            this.checkBox.setEnabled(false);
            this.preventLeaving = false;

            ToastService.open({ message: 'hi.request-successful', level: ToastLevel.SUCCESS, highlight: false });
          }).catch((response) => {
            this.handleSubmitError(response);
          });
      }).catch(response => {
        this.handleSubmitError(response);
      }).finally(() =>{
        this.formQuestionnaire.setLoading(false);
      });
  }

  handleSubmitError(response: any): void {

    if (response.body != '') {
      ToastService.open({ message: response.body, level: ToastLevel.ERROR, highlight: false });
    } else {
      ToastService.open({ ...response, highlight: false });
    }

    this.formQuestionnaire.setLocked(false);
    this.formQuestionnaire.setEditing(true);
    this.formQuestionnaire.setLocked(true);
    this.formUser.setEditing(true);
  }

  loadDynamicForm(): void {
    const seqInsuredDeclaration = SessionStorageService.getItem('seqInsuredDeclaration') as number;
    const nrSeqModel = this.context.get('nrSeqModel');
    void HttpService.get(ENDPOINTS.GET.HEALTH_DECLARATION_TERM_GENERIC)
      .then(response => {
        if (response.body) {
          const { nrTerm } = (response.body as { nrTerm: string });
          if (nrTerm) {
            this.context.set({ 'nrTerm': nrTerm });
            catchRenderException(() => this.fieldPrivacyWarning.setVisible(true));
            this.panelModal.setVisible(true);
          }
        }
      })
      .catch((response) => ToastService.open({ ...response, highlight: false }));
    void HttpService.get(ENDPOINTS.GET.HEALTH_DECLARATION_QUESTIONNAIRE, { nrSeqModel })
      .then(response => {
        this.dynamicFields.setMetadata(response.body as Metadata);
        this.formQuestionnaire.setTitle((response.body as DynamicFieldsData).title);
        this.fetchQuestionnaire.setEndpoint(ENDPOINTS.GET.HEALTH_DECLARATION_QUESTIONNAIRE_REPLIES);
        this.fetchQuestionnaire.setParameters({ nrSeqDeclaration: seqInsuredDeclaration });
      })
      .catch((response) => ToastService.open({ ...response, highlight: false }))
      .finally(() => this.formQuestionnaire.setLoading(false));

  }

}
